import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  TextField, 
  Button, 
  IconButton, 
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tabs,
  Tab
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChatIcon from '@mui/icons-material/Chat';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SchemaIcon from '@mui/icons-material/Schema';
import { useChat } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFramework } from '../../contexts/FrameworkContext';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import FrameworkSidebar from '../common/FrameworkSidebar';
import FrameworkStepDetails from './FrameworkStepDetails';

const ChatInterface = () => {
  const {
    chats,
    currentChat,
    messages,
    loading,
    /* eslint-disable no-unused-vars */
    error,
    /* eslint-enable no-unused-vars */
    typing,
    loadChats,
    loadChat,
    createChat,
    sendMessageWs,
    updateChatTitle,
    deleteChat
  } = useChat();
  
  const [newChatTitle, setNewChatTitle] = useState('');
  /* eslint-disable no-unused-vars */
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  /* eslint-enable no-unused-vars */
  const [anchorEl, setAnchorEl] = useState(null);
  const [editTitleDialogOpen, setEditTitleDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [agentInfo, setAgentInfo] = useState(null);
  const [promptInfo, setPromptInfo] = useState(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const [showPromptEdit, setShowPromptEdit] = useState(false);
  const [showFullPrompt, setShowFullPrompt] = useState(false);
  const [sidebarTab, setSidebarTab] = useState(0); // 0 for chats, 1 for frameworks
  const [selectedStep, setSelectedStep] = useState(null);
  const messagesEndRef = useRef(null);
  
  // Import the auth context and framework context
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { activeFramework } = useFramework();
  
  // Listen for framework changes to reset chat
  useEffect(() => {
    const handleFrameworkChange = (event) => {
      const { frameworkId, frameworkTitle } = event.detail;
      
      // Create a new chat for the framework
      if (frameworkId && frameworkTitle) {
        const title = `${frameworkTitle} Discussion`;
        createChat(title)
          .then(() => {
            console.log(`Created new chat for framework: ${frameworkTitle}`);
          })
          .catch(err => {
            console.error('Error creating chat for new framework:', err);
          });
      }
    };
    
    // Add event listener for framework changes
    window.addEventListener('framework-changed', handleFrameworkChange);
    
    // Clean up event listener
    return () => {
      window.removeEventListener('framework-changed', handleFrameworkChange);
    };
  }, [createChat]);

  // Load chats when authenticated
  useEffect(() => {
    // Only load chats when authenticated and not in loading state
    if (isAuthenticated && !authLoading) {
      console.log('Authentication confirmed, loading chats...');
      
      // Add a small delay to ensure auth headers are set
      const timeout = setTimeout(() => {
        loadChats();
      }, 300);
      
      return () => clearTimeout(timeout);
    }
  }, [isAuthenticated, authLoading, loadChats]);
  
  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Listen for agent info updates
  useEffect(() => {
    const handleAgentInfo = (event) => {
      const { metadata, messageId } = event.detail;
      console.log(`Received agent-info event for message ${messageId}:`, metadata);
      
      // Update agent information
      setAgentInfo({
        agent: metadata.routed_to || 'Unknown',
        reason: metadata.routing_reason || 'No reason provided',
        messageId
      });
      
      // Update prompt information
      setPromptInfo({
        systemPrompt: metadata.agent_system_prompt || 'No system prompt available',
        analysisPrompt: metadata.agent_analysis_prompt || metadata.analysis_prompt || 'No analysis prompt available',
        messageId
      });
      
      // Initialize custom prompt with the current system prompt
      setCustomPrompt(metadata.agent_system_prompt || '');
    };
    
    window.addEventListener('agent-info', handleAgentInfo);
    return () => window.removeEventListener('agent-info', handleAgentInfo);
  }, []);
  
  // Handle creating a new chat with empty context
  const handleNewChat = () => {
    createChat(newChatTitle || 'New Chat')
      .then(() => {
        // No need to manually clear messages as createChat handles this
        console.log('Created new chat with empty context');
        setNewChatTitle('');
        setIsMobileDrawerOpen(false);
      })
      .catch(err => {
        console.error('Error creating new chat:', err);
      });
  };
  
  // Handle sending a message
  const handleSendMessage = (message) => {
    if (!message.trim()) return;
    
    try {
      // No need to manually set loading state as it's handled by the ChatContext
      
      if (!currentChat) {
        // Create a new chat with the message as title
        const title = message.substring(0, 30) + (message.length > 30 ? '...' : '');
        console.log(`Creating new chat: "${title}"`);
        
        // Create chat via REST API first to avoid WebSocket duplication issues
        createChat(title)
          .then((newChat) => {
            console.log(`Chat created with ID: ${newChat.id}, sending message...`);
            
            // Store the newly created chat ID to prevent duplicates
            window.lastCreatedChatId = newChat.id;
            
            // Send message after a small delay to ensure chat is created
            setTimeout(() => {
              sendMessageWs(message, newChat.id);
            }, 500);
          })
          .catch(err => {
            console.error('Error creating chat:', err);
            // Error handling is managed by the ChatContext
          });
      } else {
        console.log(`Sending message to existing chat: ${currentChat.id}`);
        sendMessageWs(message, currentChat.id);
      }
    } catch (err) {
      console.error('Error in handleSendMessage:', err);
      // Error handling is managed by the ChatContext
    }
  };
  
  // Handle opening chat options menu
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  // Handle closing chat options menu
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  // Handle opening edit title dialog
  const handleEditTitle = () => {
    setEditedTitle(currentChat?.title || '');
    setEditTitleDialogOpen(true);
    handleMenuClose();
  };
  
  // Handle saving edited title
  const handleSaveTitleEdit = () => {
    if (currentChat && editedTitle.trim()) {
      updateChatTitle(currentChat.id, editedTitle);
    }
    setEditTitleDialogOpen(false);
  };
  
  // Handle opening delete confirmation dialog
  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };
  
  // Handle deleting chat
  const handleDeleteChat = () => {
    if (currentChat) {
      deleteChat(currentChat.id);
    }
    setDeleteDialogOpen(false);
  };
  
  // Handle framework step click
  const handleStepClick = (step) => {
    setSelectedStep(step);
    // Optionally scroll to framework step details
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };
  
  // Handle sidebar tab change
  const handleTabChange = (event, newValue) => {
    setSidebarTab(newValue);
    
    // When switching to frameworks tab, reset the chat interface
    if (newValue === 1) {
      // If there's an active framework, create a new chat for it
      if (activeFramework) {
        const title = `${activeFramework.title} Discussion`;
        createChat(title)
          .then(() => {
            console.log(`Created new chat for framework: ${activeFramework.title}`);
          })
          .catch(err => {
            console.error('Error creating chat for framework view:', err);
          });
      } else {
        // If no active framework, create a generic framework chat
        createChat("Framework Discussion")
          .then(() => {
            console.log("Created new chat for framework exploration");
          })
          .catch(err => {
            console.error('Error creating new framework chat:', err);
          });
      }
    }
  };

  // Handle resending a query with a custom prompt
  const handleReaskWithPrompt = (customPromptText) => {
    if (!currentChat || !promptInfo) {
      console.error('Cannot reask without an active chat or prompt information');
      return;
    }
    
    // Get the last user message from the conversation
    const userMessages = messages.filter(msg => msg.role === 'user');
    const lastUserMessage = userMessages[userMessages.length - 1];
    
    if (!lastUserMessage) {
      console.error('No user message found to reask');
      return;
    }
    
    // Construct message with the custom prompt request
    const message = {
      original_message: lastUserMessage.content,
      custom_prompt: customPromptText,
      is_custom_prompt: true
    };
    
    console.log('Reasking with custom prompt:', message);
    
    // Prepare the message with custom prompt
    
    // Send message with the custom prompt
    try {
      sendMessageWs(JSON.stringify(message), currentChat.id);
      
      // Hide the prompt edit after sending
      setShowPromptEdit(false);
    } catch (err) {
      console.error('Error sending message with custom prompt:', err);
    }
  };
  
  return (
    <Box sx={{ height: 'calc(75vh)', display: 'flex' }}>
      {/* Sidebar with Tabs - Desktop */}
      <Box
        sx={{
          width: 300,
          flexShrink: 0,
          borderRight: '1px solid #e0e0e0',
          display: { xs: 'none', sm: 'flex' },
          bgcolor: '#f9f9f9',
          flexDirection: 'column'
        }}
      >
        {/* Tabs for switching between chats and frameworks */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={sidebarTab} 
            onChange={handleTabChange} 
            variant="fullWidth"
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: 'black',
              },
              '& .Mui-selected': {
                color: 'black !important',
              }
            }}
          >
            <Tab 
              label="Chats" 
              icon={<ChatIcon />} 
              iconPosition="start" 
              sx={{ textTransform: 'none' }} 
            />
            <Tab 
              label="Frameworks" 
              icon={<SchemaIcon />} 
              iconPosition="start" 
              sx={{ textTransform: 'none' }} 
            />
          </Tabs>
        </Box>
        
        {/* Content area for the selected tab */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
          {/* Chats Tab */}
          {sidebarTab === 0 && (
            <Box sx={{ p: 2 }}>
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  fullWidth
                  onClick={handleNewChat}
                  sx={{ 
                    backgroundColor: 'black',
                    '&:hover': {
                      backgroundColor: '#333',
                    },
                    mb: 1
                  }}
                >
                  New Chat
                </Button>
                
                <TextField
                  placeholder="Chat name (optional)"
                  size="small"
                  fullWidth
                  value={newChatTitle}
                  onChange={(e) => setNewChatTitle(e.target.value)}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'black',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'black',
                      },
                    }
                  }}
                />
              </Box>
              
              <List>
                {chats.map((chat) => (
                  <ListItem 
                    key={chat.id} 
                    disablePadding
                    sx={{
                      mb: 1,
                      bgcolor: currentChat?.id === chat.id ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
                      borderRadius: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        width: '100%',
                        alignItems: 'center'
                      }}
                    >
                      <ListItemButton 
                        onClick={() => loadChat(chat.id)}
                        sx={{ 
                          borderRadius: 1,
                          flexGrow: 1,
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.05)',
                          }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: '40px' }}>
                          <ChatIcon />
                        </ListItemIcon>
                        <ListItemText 
                          primary={chat.title}
                          primaryTypographyProps={{ 
                            noWrap: true,
                            sx: { 
                              fontWeight: currentChat?.id === chat.id ? 600 : 400
                            }
                          }}
                        />
                      </ListItemButton>
                      
                      {/* Edit and Delete buttons */}
                      <Box sx={{ display: 'flex', pr: 1 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            // First load the chat to set it as current
                            loadChat(chat.id).then(() => {
                              setEditedTitle(chat.title);
                              setEditTitleDialogOpen(true);
                            });
                          }}
                          sx={{ mr: 0.5 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Delete directly without setting current
                            deleteChat(chat.id);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          
          {/* Frameworks Tab */}
          {sidebarTab === 1 && (
            <FrameworkSidebar onStepClick={handleStepClick} />
          )}
        </Box>
      </Box>
      
      {/* Main Split View Area */}
      <Box sx={{ flexGrow: 1, display: 'flex', height: '100%' }}>
        {/* Left Side - Chat Area */}
        <Box sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column',
          width: '60%',
          borderRight: '1px solid #e0e0e0'
        }}>
          {/* Chat Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              borderBottom: '1px solid #e0e0e0',
              bgcolor: '#ffffff'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {currentChat ? currentChat.title : 'New Chat'}
            </Typography>
            
            {currentChat && (
              <>
                <IconButton onClick={handleMenuOpen}>
                  <MoreVertIcon />
                </IconButton>
                
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  <MenuItem onClick={handleEditTitle}>
                    <ListItemIcon>
                      <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <Typography variant="inherit">Edit Title</Typography>
                  </MenuItem>
                  <MenuItem onClick={handleOpenDeleteDialog}>
                    <ListItemIcon>
                      <DeleteIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <Typography variant="inherit" color="error">Delete Chat</Typography>
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
          
          {/* Messages Area */}
          <Box sx={{ flexGrow: 1, p: 3, overflowY: 'auto', bgcolor: '#ffffff' }}>
            {messages.length === 0 && !selectedStep ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  opacity: 0.7
                }}
              >
                <ChatIcon sx={{ fontSize: 60, mb: 2, color: '#aaa' }} />
                <Typography variant="h6" color="text.secondary">
                  Start a conversation with StateShift Pathfinder
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Ask about frameworks, professional development, or community building
                </Typography>
              </Box>
            ) : (
              <>
                {/* Display selected framework step details if any */}
                {selectedStep && (
                  <FrameworkStepDetails step={selectedStep} />
                )}
                
                {/* Display chat messages */}
                <MessageList messages={messages} typing={typing} />
              </>
            )}
            <div ref={messagesEndRef} />
          </Box>
          
          {/* Message Input */}
          <Box
            sx={{
              p: 2,
              borderTop: '1px solid #e0e0e0',
              bgcolor: '#ffffff'
            }}
          >
            <MessageInput onSendMessage={handleSendMessage} disabled={loading} />
          </Box>
        </Box>
        
        {/* Right Side - Information Panel */}
        <Box sx={{ 
          width: '40%', 
          display: 'flex', 
          flexDirection: 'column',
          bgcolor: '#f9f9f9',
        }}>
          {/* Info Panel Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              borderBottom: '1px solid #e0e0e0',
              bgcolor: '#ffffff'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Information Panel
            </Typography>
          </Box>
          
          {/* Info Content Area */}
          <Box sx={{ flexGrow: 1, p: 3, overflowY: 'auto' }}>
            {/* Agent Information Section */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Agent Information
              </Typography>
              {agentInfo ? (
                <div>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      <strong>Agent:</strong> {agentInfo.agent}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Reason:</strong> {agentInfo.reason}
                    </Typography>
                  </Box>
                  <Typography variant="body2">
                    This query was processed by the <strong>{agentInfo.agent}</strong> agent.
                  </Typography>
                </div>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No agent has been used yet. Send a message to see which agent processes your query.
                </Typography>
              )}
            </Paper>
            
            {/* Prompt Information Section */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 0 }}>
                  Prompt Information
                </Typography>
                {promptInfo && (
                  <Button 
                    size="small" 
                    variant="outlined" 
                    onClick={() => setShowPromptEdit(!showPromptEdit)}
                    sx={{ 
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      borderColor: 'black',
                      color: 'black',
                      '&:hover': {
                        borderColor: 'black',
                        backgroundColor: 'rgba(0,0,0,0.04)'
                      }
                    }}
                  >
                    {showPromptEdit ? 'Hide Editor' : 'Edit Prompt'}
                  </Button>
                )}
              </Box>
              
              {promptInfo ? (
                <>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>System Prompt:</strong>
                  </Typography>
                  <Box sx={{ position: 'relative', mb: 2 }}>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2, 
                        maxHeight: showFullPrompt ? 'none' : '250px', 
                        overflow: showFullPrompt ? 'visible' : 'auto',
                        backgroundColor: '#f5f5f5',
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}
                    >
                      {promptInfo.systemPrompt}
                    </Paper>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'center',
                        mt: 1
                      }}
                    >
                      <Button 
                        size="small" 
                        onClick={() => setShowFullPrompt(!showFullPrompt)}
                        startIcon={showFullPrompt ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{ 
                          textTransform: 'none',
                          color: 'text.secondary',
                          fontSize: '0.75rem'
                        }}
                      >
                        {showFullPrompt ? 'Show Less' : 'Show Full Prompt'}
                      </Button>
                    </Box>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Box sx={{ mb: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {showPromptEdit ? 'Edit System Prompt:' : 'Custom Prompt:'}
                      </Typography>
                    </Box>
                    
                    {showPromptEdit ? (
                      <>
                        <TextField
                          fullWidth
                          multiline
                          rows={6}
                          variant="outlined"
                          placeholder="Enter your custom system prompt here..."
                          value={customPrompt}
                          onChange={(e) => setCustomPrompt(e.target.value)}
                          sx={{ 
                            mb: 1,
                            '& .MuiOutlinedInput-root': {
                              fontFamily: 'monospace',
                              fontSize: '0.85rem'
                            }
                          }}
                        />
                        <Button 
                          variant="contained"
                          sx={{ 
                            backgroundColor: 'black',
                            '&:hover': {
                              backgroundColor: '#333'
                            }
                          }}
                          onClick={() => handleReaskWithPrompt(customPrompt)}
                        >
                          Reask with Custom Prompt
                        </Button>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        Click "Edit Prompt" to customize the system prompt and reask your question.
                      </Typography>
                    )}
                  </Box>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No prompts have been used yet. Send a message to see the prompts used to generate responses.
                </Typography>
              )}
            </Paper>
          </Box>
        </Box>
      </Box>
      
      {/* Edit Title Dialog */}
      <Dialog open={editTitleDialogOpen} onClose={() => setEditTitleDialogOpen(false)}>
        <DialogTitle>Edit Chat Title</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="chat-title"
            label="Title"
            type="text"
            fullWidth
            variant="outlined"
            value={editedTitle}
            onChange={(e) => setEditedTitle(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'black',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'black',
                },
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTitleDialogOpen(false)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveTitleEdit}
            sx={{ 
              color: 'black',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Chat</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this chat? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteChat} 
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatInterface;